import React, { useState, useEffect, useMemo } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Panel,
  MarkerType,
  Handle,
  Position
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { 
  Activity, 
  Zap, 
  Users, 
  Cpu,
  Box,
  RefreshCw,
  Maximize2,
  Search,
  Filter,
  Info,
  Radio,
  LayoutGrid,
  Workflow
} from 'lucide-react'
import { apiFetch } from '../config'

// Custom node component with CRUCIX styling
const AgentNode = ({ data }) => {
  const isActive = data.status === 'active'
  const color = data.categoryColor || '#6366f1'
  
  return (
    <div className="group relative">
      {/* Node container */}
      <div 
        className={`
          crucix-card p-3 min-w-[140px] max-w-[200px]
          transition-all duration-300 cursor-pointer
          ${isActive ? 'crucix-card-active animate-glow' : 'crucix-card-hover'}
        `}
        style={{
          borderLeft: `3px solid ${color}`,
        }}
      >
        {/* Category badge */}
        <div 
          className="absolute -top-2 -right-2 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider crucix-badge"
          style={{ 
            backgroundColor: `${color}20`,
            borderColor: `${color}40`,
            color: color
          }}
        >
          {data.category}
        </div>
        
        {/* Status indicator */}
        <div className="flex items-center gap-2 mb-2">
          <div 
            className={`w-2 h-2 crucix-status-dot ${isActive ? 'crucix-status-online' : 'bg-gray-500'}`}
            style={{
              backgroundColor: isActive ? '#00d084' : '#4a5568',
              clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
            }}
          />
          <span className="text-[10px] text-text-secondary font-mono uppercase">
            {data.status || 'idle'}
          </span>
        </div>
        
        {/* Agent name */}
        <h4 className="font-display text-sm font-semibold text-text-primary mb-1 truncate">
          {data.name}
        </h4>
        
        {/* Agent ID */}
        <p className="text-[10px] text-text-muted font-mono truncate">
          {data.id}
        </p>
        
        {/* Skills count */}
        {data.skills?.length > 0 && (
          <div className="mt-2 flex items-center gap-1 text-[10px] text-text-secondary">
            <Cpu className="w-3 h-3" />
            <span>{data.skills.length} skills</span>
          </div>
        )}
        
        {/* Activation count */}
        {data.activation_count > 0 && (
          <div className="mt-1 text-[10px] text-text-cyan">
            Activated: {data.activation_count}x
          </div>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute z-50 left-full ml-2 top-0 w-64 crucix-card p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
        <h5 className="font-display text-sm font-bold text-text-primary mb-2">{data.name}</h5>
        <p className="text-xs text-text-secondary mb-2">{data.description || 'No description'}</p>
        {data.skills?.length > 0 && (
          <div className="mb-2">
            <span className="text-[10px] text-text-muted uppercase">Skills:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {data.skills.slice(0, 5).map((skill, i) => (
                <span key={i} className="text-[10px] px-1.5 py-0.5 bg-surface-panel text-text-secondary rounded">
                  {skill}
                </span>
              ))}
              {data.skills.length > 5 && (
                <span className="text-[10px] text-text-muted">+{data.skills.length - 5}</span>
              )}
            </div>
          </div>
        )}
        <button className="w-full crucix-btn-primary text-xs py-1.5 mt-2">
          Activate Agent
        </button>
      </div>
      
      {/* ReactFlow handles */}
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-accent" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-accent" />
    </div>
  )
}

const NeuralMap = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'category'
  const [stats, setStats] = useState({ total: 0, categories: {}, active: 0 })
  const [wsConnected, setWsConnected] = useState(false)

  const nodeTypes = useMemo(() => ({ agent: AgentNode }), [])

  // Category colors for CRUCIX theme
  const categoryColors = {
    'academic': '#00d4ff',
    'design': '#ff6b00',
    'engineering': '#00d084',
    'game-development': '#ff2a2a',
    'marketing': '#b87333',
    'paid-media': '#c0c0c0',
    'product': '#8b5cf6',
    'sales': '#f59e0b',
    'support': '#3b82f6',
    'testing': '#10b981',
    'specialized': '#ec4899',
    'strategy': '#6366f1',
    'spatial': '#14b8a6',
    'general': '#00a8e8'
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      
      // Fetch agency agents
      const agentsRes = await apiFetch('/agency-agents')
      const agentsData = await agentsRes.json()
      
      // Fetch skills
      const skillsRes = await apiFetch('/skills')
      const skillsData = await skillsRes.json()
      
      const agents = agentsData.agents || []
      const skills = skillsData.skills || []
      
      // Calculate stats
      const categories = {}
      agents.forEach(a => {
        categories[a.category] = (categories[a.category] || 0) + 1
      })
      
      setStats({
        total: agents.length,
        categories,
        active: agents.filter(a => a.status === 'active').length,
        skills: skills.length
      })
      
      // Create nodes based on view mode
      let layoutNodes = []
      
      if (viewMode === 'category') {
        // Group by category in columns
        const categoryList = Object.keys(categories).sort()
        const colWidth = 250
        const rowHeight = 100
        
        categoryList.forEach((category, colIndex) => {
          const categoryAgents = agents.filter(a => a.category === category)
          
          categoryAgents.forEach((agent, rowIndex) => {
            layoutNodes.push({
              id: agent.id,
              position: { 
                x: colIndex * colWidth + 50, 
                y: rowIndex * rowHeight + 50 
              },
              type: 'agent',
              data: {
                ...agent,
                categoryColor: categoryColors[agent.category] || '#00a8e8',
                skills: Array.isArray(agent.skills) ? agent.skills : JSON.parse(agent.skills || '[]')
              }
            })
          })
        })
      } else {
        // Grid layout
        const cols = 6
        const colWidth = 220
        const rowHeight = 120
        
        agents.forEach((agent, index) => {
          const col = index % cols
          const row = Math.floor(index / cols)
          
          layoutNodes.push({
            id: agent.id,
            position: { 
              x: col * colWidth + 50, 
              y: row * rowHeight + 50 
            },
            type: 'agent',
            data: {
              ...agent,
              categoryColor: categoryColors[agent.category] || '#00a8e8',
              skills: Array.isArray(agent.skills) ? agent.skills : JSON.parse(agent.skills || '[]')
            }
          })
        })
      }
      
      // Filter by search and category
      let filteredNodes = layoutNodes
      
      if (searchTerm) {
        filteredNodes = filteredNodes.filter(n => 
          n.data.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          n.data.id.toLowerCase().includes(searchTerm.toLowerCase())
        )
      }
      
      if (selectedCategory !== 'all') {
        filteredNodes = filteredNodes.filter(n => n.data.category === selectedCategory)
      }
      
      setNodes(filteredNodes)
      setEdges([]) // No edges for now
      setLoading(false)
    } catch (error) {
      console.error('Failed to fetch neural data:', error)
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [viewMode, searchTerm, selectedCategory])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-10 h-10 animate-spin text-accent" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="crucix-header px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div>
            <h1 className="crucix-section-title text-2xl">
              <Workflow className="w-6 h-6 text-accent" />
              Neural Map
            </h1>
            <p className="text-text-secondary text-sm mt-1">
              {stats.total} agents • {stats.active} active • {Object.keys(stats.categories).length} categories
            </p>
          </div>
          
          {/* WebSocket status */}
          <div className={`flex items-center gap-2 px-3 py-1 crucix-badge ${wsConnected ? 'crucix-badge-online' : 'crucix-badge-idle'}`}>
            <Radio className={`w-3 h-3 ${wsConnected ? 'animate-pulse' : ''}`} />
            <span className="text-xs">{wsConnected ? 'Live' : 'Offline'}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-surface border border-border rounded text-sm text-text-primary placeholder-text-muted focus:border-accent focus:outline-none w-64"
            />
          </div>
          
          {/* Category filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:border-accent focus:outline-none"
          >
            <option value="all">All Categories</option>
            {Object.entries(stats.categories).sort((a, b) => b[1] - a[1]).map(([cat, count]) => (
              <option key={cat} value={cat}>
                {cat.replace('-', ' ')} ({count})
              </option>
            ))}
          </select>
          
          {/* View mode toggle */}
          <div className="flex bg-surface border border-border rounded overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 flex items-center gap-2 text-sm transition-colors ${viewMode === 'grid' ? 'bg-accent text-background' : 'text-text-secondary hover:text-text-primary'}`}
            >
              <LayoutGrid className="w-4 h-4" />
              Grid
            </button>
            <button
              onClick={() => setViewMode('category')}
              className={`px-3 py-2 flex items-center gap-2 text-sm transition-colors ${viewMode === 'category' ? 'bg-accent text-background' : 'text-text-secondary hover:text-text-primary'}`}
            >
              <Box className="w-4 h-4" />
              Category
            </button>
          </div>
          
          <button
            onClick={fetchData}
            className="p-2 bg-surface border border-border rounded hover:border-accent transition-colors"
          >
            <RefreshCw className="w-5 h-5 text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="px-6 py-3 bg-surface-panel border-b border-border flex items-center gap-6 overflow-x-auto">
        {Object.entries(stats.categories).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([cat, count]) => (
          <div 
            key={cat}
            className="flex items-center gap-2 text-sm cursor-pointer hover:opacity-80"
            onClick={() => setSelectedCategory(cat === selectedCategory ? 'all' : cat)}
          >
            <div 
              className="w-3 h-3 crucix-status-dot"
              style={{ 
                backgroundColor: categoryColors[cat] || '#00a8e8',
                clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
              }}
            />
            <span className={`capitalize ${selectedCategory === cat ? 'text-text-cyan font-semibold' : 'text-text-secondary'}`}>
              {cat.replace('-', ' ')}
            </span>
            <span className="text-text-muted font-mono">{count}</span>
          </div>
        ))}
      </div>

      {/* Main content */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onNodesChange}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.2}
          maxZoom={2}
          attributionPosition="bottom-left"
        >
          <Background 
            color="#1a1a2e" 
            gap={30} 
            size={1}
            style={{ opacity: 0.5 }}
          />
          <Controls className="bg-surface border border-border" />
          
          {/* Info panel */}
          <Panel position="top-right" className="m-4">
            <div className="crucix-card p-4 space-y-3 w-64">
              <h3 className="font-display text-sm font-bold text-text-primary uppercase tracking-wider">
                Network Stats
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Total Agents</span>
                  <span className="text-text-primary font-mono font-bold">{stats.total}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Active Now</span>
                  <span className="text-success font-mono font-bold">{stats.active}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Categories</span>
                  <span className="text-text-primary font-mono">{Object.keys(stats.categories).length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Skills Available</span>
                  <span className="text-text-primary font-mono">{stats.skills}</span>
                </div>
              </div>
              
              {searchTerm && (
                <div className="pt-2 border-t border-border">
                  <p className="text-xs text-text-muted">
                    Showing {nodes.length} results for "{searchTerm}"
                  </p>
                </div>
              )}
            </div>
          </Panel>
        </ReactFlow>
      </div>
    </div>
  )
}

export default NeuralMap